/**
 * Reorder suggestions — `/purchasing/reorder-suggestions`.
 *
 * The nightly calculation's output, grouped by preferred supplier. Every
 * net-position input is shown as the run snapshotted it, so the number is
 * auditable rather than magic. Selected rows convert to supplier-grouped
 * draft POs (`POST /reorder-suggestions/convert`, grouping is server-side) or
 * are dismissed with a closed machine reason until the next run.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { formatDateTime, formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  REORDER_DISMISS_REASONS,
  REORDER_SUGGESTION_STATUSES,
  calculateReorderSuggestions,
  convertReorderSuggestions,
  dismissReorderSuggestions,
  type ReorderConversionRequest,
  type ReorderConversionResult,
  type ReorderDismissReason,
  type ReorderSuggestion,
  type ReorderSuggestionStatus,
} from "../api";
import { purchasingKeys, useReorderSuggestionList } from "../queries";
import { useSupplierNames, useVariantSkus, useWarehouseNames } from "../components/lookup-names";
import { handleProblem } from "../problem-errors";
import { enumLabel } from "../labels";
import { QTY_SCALE } from "../decimal";

export interface ReorderSuggestionsPageProps {
  onOpenPurchaseOrder: (purchaseOrderId: string) => void;
  canConvert?: boolean;
  canDismiss?: boolean;
  canCalculate?: boolean;
  canReadSuppliers?: boolean;
  canReadVariants?: boolean;
  canReadWarehouses?: boolean;
  pageLimit?: number;
}

export function ReorderSuggestionsPage({
  onOpenPurchaseOrder,
  canConvert = false,
  canDismiss = false,
  canCalculate = false,
  canReadSuppliers = true,
  canReadVariants = true,
  canReadWarehouses = true,
  pageLimit,
}: ReorderSuggestionsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<ReorderSuggestionStatus>("open");
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>("__all__");
  const [selected, setSelected] = useState<string[]>([]);
  const [expectedDate, setExpectedDate] = useState("");
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<ReorderDismissReason>("not_required");
  const [conversion, setConversion] = useState<ReorderConversionResult | null>(null);
  const [runAccepted, setRunAccepted] = useState(false);

  const { supplierName, parties } = useSupplierNames(canReadSuppliers);
  const { variantSku } = useVariantSkus(canReadVariants);
  const { knownWarehouseName, warehouses } = useWarehouseNames(canReadWarehouses);

  const filters = useMemo(
    () => ({
      status,
      ...(supplierId === null || supplierId === "" ? {} : { supplier_id: supplierId }),
      ...(warehouseId === "__all__" ? {} : { warehouse_id: warehouseId }),
    }),
    [status, supplierId, warehouseId],
  );
  const query = useReorderSuggestionList(filters, pageLimit);
  const rows = query.rows;

  const lastRunAt = rows[0]?.calculated_at;

  const groups = useMemo(() => {
    const bySupplier = new Map<string, ReorderSuggestion[]>();
    for (const row of rows) {
      const bucket = bySupplier.get(row.supplier_id) ?? [];
      bucket.push(row);
      bySupplier.set(row.supplier_id, bucket);
    }
    return [...bySupplier.entries()];
  }, [rows]);

  const selectable = (row: ReorderSuggestion): boolean => row.status === "open";
  const toggle = (id: string): void =>
    setSelected((current) =>
      current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id],
    );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: purchasingKeys.reorderSuggestions.all });
    void queryClient.invalidateQueries({ queryKey: purchasingKeys.purchaseOrders.all });
  };

  const convertMutation = useIdempotentMutation<ReorderConversionResult, ReorderConversionRequest>({
    mutationFn: (input, key) => convertReorderSuggestions(input, key),
    onSuccess: (result) => {
      setConversion(result);
      setSelected([]);
      invalidate();
    },
    onError: (error) => {
      handleProblem(error, t);
    },
  });

  const calculateMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof calculateReorderSuggestions>>,
    void
  >({
    mutationFn: (_input, key) => calculateReorderSuggestions(key),
    onSuccess: () => {
      // The run was accepted, not finished. Refreshing the list is how its
      // result arrives; saying "recalculated" here would be a lie.
      setRunAccepted(true);
      invalidate();
    },
    onError: (error) => {
      handleProblem(error, t);
    },
  });

  const dismissMutation = useIdempotentMutation<
    Awaited<ReturnType<typeof dismissReorderSuggestions>>,
    { suggestion_ids: string[]; reason: ReorderDismissReason }
  >({
    mutationFn: (input, key) => dismissReorderSuggestions(input, key),
    onSuccess: () => {
      setDismissOpen(false);
      setSelected([]);
      invalidate();
    },
    onError: (error) => {
      handleProblem(error, t);
    },
  });

  const renderQty = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, QTY_SCALE);
    } catch {
      return value;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {t("purchasing.reorder.title", "Reorder suggestions")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lastRunAt === undefined
              ? t("purchasing.reorder.noRun", "No calculation run has produced suggestions yet.")
              : t("purchasing.reorder.lastRun", "Last calculation {{when}}", {
                  when: (() => {
                    try {
                      return formatDateTime(lastRunAt, i18n.language);
                    } catch {
                      return lastRunAt;
                    }
                  })(),
                })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {runAccepted ? (
            <p role="status" className="text-sm text-muted-foreground">
              {t(
                "purchasing.reorder.runAccepted",
                "A run was queued. Its suggestions appear here when it finishes.",
              )}
            </p>
          ) : null}
          {canCalculate ? (
            <Button
              type="button"
              variant="outline"
              loading={calculateMutation.isPending}
              disabled={calculateMutation.isPending}
              onClick={() => calculateMutation.mutate()}
            >
              {t("purchasing.reorder.calculate", "Recalculate now")}
            </Button>
          ) : null}
          {canDismiss ? (
            <Button
              type="button"
              variant="outline"
              disabled={selected.length === 0}
              onClick={() => setDismissOpen(true)}
            >
              {t("purchasing.reorder.dismissSelected", "Dismiss {{count}} selected", {
                count: selected.length,
              })}
            </Button>
          ) : null}
          {canConvert ? (
            <Button
              type="button"
              loading={convertMutation.isPending}
              disabled={selected.length === 0 || convertMutation.isPending}
              onClick={() =>
                convertMutation.mutate({
                  suggestion_ids: selected,
                  ...(expectedDate === "" ? {} : { expected_delivery_date: expectedDate }),
                })
              }
            >
              {t("purchasing.reorder.convertSelected", "Convert {{count}} to draft POs", {
                count: selected.length,
              })}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-reorder-status">
            {t("purchasing.reorder.filters.status", "Status")}
          </Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as ReorderSuggestionStatus)}
          >
            <SelectTrigger id="purchasing-reorder-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REORDER_SUGGESTION_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {enumLabel(t, "reorderStatus", value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canReadSuppliers ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-reorder-supplier">
              {t("purchasing.reorder.filters.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-reorder-supplier"
              options={parties.map((party) => ({ value: party.id, label: party.display_name }))}
              value={supplierId}
              onChange={setSupplierId}
              placeholder={t("purchasing.common.anySupplier", "Any supplier")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
            />
          </div>
        ) : null}
        {canReadWarehouses ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-reorder-warehouse">
              {t("purchasing.reorder.filters.warehouse", "Warehouse")}
            </Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger id="purchasing-reorder-warehouse">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("purchasing.common.all", "All")}</SelectItem>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-reorder-expected">
            {t("purchasing.reorder.filters.expected", "Expected delivery on new POs")}
          </Label>
          <Input
            id="purchasing-reorder-expected"
            type="date"
            dir="ltr"
            value={expectedDate}
            onChange={(event) => setExpectedDate(event.target.value)}
          />
        </div>
      </div>

      {conversion !== null ? (
        <Card data-testid="reorder-conversion-result">
          <CardHeader>
            <CardTitle>
              <h2>
                {t("purchasing.reorder.converted", "{{count}} draft purchase order(s) created", {
                  count: conversion.purchase_orders.length,
                })}
              </h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {conversion.purchase_orders.map((order) => (
              <Button
                key={order.id}
                type="button"
                variant="link"
                className="h-auto justify-start p-0"
                onClick={() => onOpenPurchaseOrder(order.id)}
              >
                <span dir="ltr" className="font-mono">
                  {order.po_number}
                </span>
                <ArrowRight className="size-4 rtl:-scale-x-100" aria-hidden="true" />
              </Button>
            ))}
          </CardContent>
        </Card>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t("purchasing.common.loading", "Loading…")}
        </p>
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-destructive">
            {t("purchasing.reorder.loadError", "Suggestions could not be loaded.")}
          </p>
          <Button type="button" variant="outline" onClick={() => query.refetch()}>
            {t("actions.retry")}
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <p className="rounded-md border bg-muted/40 p-3 text-sm">
          {t("purchasing.reorder.empty", "No suggestions in this view.")}
        </p>
      ) : (
        groups.map(([groupSupplierId, groupRows]) => {
          const openRows = groupRows.filter(selectable);
          const allSelected =
            openRows.length > 0 && openRows.every((row) => selected.includes(row.id));
          return (
            <Card key={groupSupplierId}>
              <CardHeader className="flex-row items-center justify-between gap-3">
                <CardTitle>
                  <h2 className="flex items-center gap-2 text-base">
                    {supplierName(groupSupplierId)}
                    <Badge variant="outline">
                      {t("purchasing.reorder.groupCount", "{{count}} suggestion(s)", {
                        count: groupRows.length,
                      })}
                    </Badge>
                  </h2>
                </CardTitle>
                {openRows.length > 0 && (canConvert || canDismiss) ? (
                  <Label className="flex items-center gap-2 text-xs font-normal">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() =>
                        setSelected((current) => {
                          const ids = openRows.map((row) => row.id);
                          return allSelected
                            ? current.filter((candidate) => !ids.includes(candidate))
                            : [...new Set([...current, ...ids])];
                        })
                      }
                      aria-label={t("purchasing.reorder.selectGroup", "Select all for {{name}}", {
                        name: supplierName(groupSupplierId),
                      })}
                    />
                    {t("purchasing.reorder.selectAll", "Select all")}
                  </Label>
                ) : null}
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {groupRows.map((row) => (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-center gap-3 rounded-md border p-2 text-sm"
                    data-testid={`reorder-row-${row.id}`}
                  >
                    {canConvert || canDismiss ? (
                      <Checkbox
                        checked={selected.includes(row.id)}
                        disabled={!selectable(row)}
                        onCheckedChange={() => toggle(row.id)}
                        aria-label={t("purchasing.reorder.selectRow", "Select {{sku}}", {
                          sku: variantSku(row.variant_id),
                        })}
                      />
                    ) : null}
                    <span dir="ltr" className="font-mono font-medium">
                      {variantSku(row.variant_id)}
                    </span>
                    {canReadWarehouses ? (
                      <Badge variant="outline">
                        {knownWarehouseName(row.warehouse_id) ??
                          t("purchasing.reorder.otherBranch", "Warehouse in another branch")}
                      </Badge>
                    ) : null}
                    {/* Numeric net-position equation stays LTR inside RTL text. */}
                    <span dir="ltr" className="text-xs text-muted-foreground tabular-nums">
                      {t("purchasing.reorder.onHand", "on hand")} {renderQty(row.on_hand_qty)} +{" "}
                      {t("purchasing.reorder.onOrder", "on order")} {renderQty(row.on_order_qty)} −{" "}
                      {t("purchasing.reorder.reserved", "reserved")} {renderQty(row.reserved_qty)} −{" "}
                      {t("purchasing.reorder.backorder", "backorder")}{" "}
                      {renderQty(row.backorder_qty)} = {renderQty(row.net_position_qty)}
                    </span>
                    <Badge variant="secondary">
                      {t("purchasing.reorder.rop", "ROP")}{" "}
                      <span dir="ltr">{renderQty(row.reorder_point_qty)}</span>
                    </Badge>
                    <Badge variant="default">
                      {t("purchasing.reorder.suggested", "Suggested")}{" "}
                      <span dir="ltr">{renderQty(row.suggested_order_qty)}</span>
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {t("purchasing.reorder.moqLead", "MOQ {{moq}} · lead {{days}}d", {
                        moq: renderQty(row.minimum_order_qty),
                        days: row.lead_time_days,
                      })}
                    </span>
                    {row.status !== "open" ? (
                      <Badge variant="outline">{enumLabel(t, "reorderStatus", row.status)}</Badge>
                    ) : null}
                    {row.converted_purchase_order_id !== undefined ? (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() =>
                          onOpenPurchaseOrder(row.converted_purchase_order_id as string)
                        }
                      >
                        {t("purchasing.reorder.openConverted", "Open converted PO")}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}

      {query.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="outline"
            loading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {t("table.loadMore")}
          </Button>
        </div>
      ) : null}

      <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("purchasing.reorder.dismissTitle", "Dismiss suggestions")}</DialogTitle>
            <DialogDescription>
              {t(
                "purchasing.reorder.dismissBody",
                "Dismissed rows stay auditable and are hidden until the next calculation run.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-reorder-reason">
              {t("purchasing.reorder.reason", "Reason")}
            </Label>
            <Select
              value={dismissReason}
              onValueChange={(value) => setDismissReason(value as ReorderDismissReason)}
            >
              <SelectTrigger id="purchasing-reorder-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REORDER_DISMISS_REASONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {enumLabel(t, "reorderDismissReason", value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDismissOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={dismissMutation.isPending}
              disabled={dismissMutation.isPending}
              onClick={() =>
                dismissMutation.mutate({ suggestion_ids: selected, reason: dismissReason })
              }
            >
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
